
/**
 * Module dependencies.
 */

import { authRoutes, myRevigresRoutes } from 'src/core/routes';
import { color, media, units } from 'src/styles/utils';
import { revokeToken } from 'src/api/auth/revoke-token';
import { theme } from 'styled-tools';
import { useAuth } from 'src/context/auth/context';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useMutation } from 'react-query';
import { useNewsletter } from 'src/api/app/forms/newsletter/use-newsletter';
import { useRouter } from 'next/router';
import { useSnackbar } from 'src/context/snackbar/context';
import { useTranslate } from 'src/core/utils/translator';
import { userType } from 'src/core/user-config';
import { withSession } from 'src/core/utils/session';
import Box from 'src/components/core/layout/box';
import Button from 'src/components/core/buttons/button';
import CardTitle from 'src/components/core/typography/card-title';
import ChangePasswordSidebar from 'src/components/my-revigres/dashboard/change-password-sidebar';
import ConfirmAccount from 'src/components/my-revigres/dashboard/confirm-account';
import Container from 'src/components/core/layout/container';
import MenuLink from 'src/components/my-revigres/dashboard/menu-link';
import Metatags from 'src/components/metatags';
import Permission from 'src/components/permission';
import React, { ReactElement, useCallback, useState } from 'react';
import SwitchButton from 'src/components/core/buttons/switch-button';
import Type from 'src/components/core/typography';
import UserDataSidebar from 'src/components/my-revigres/dashboard/user-data-sidebar';
import favoriteIcon from 'src/assets/svg/favorite.svg';
import isEmpty from 'lodash/isEmpty';
import lockIcon from 'src/assets/svg/lock.svg';
import projectsIcon from 'src/assets/svg/projects.svg';
import settingsIcon from 'src/assets/svg/settings.svg';
import styled from 'styled-components';
import useNavbarTheme from 'src/context/navbar-theme/use-navbar-theme';
import usersIcon from 'src/assets/svg/users.svg';

/**
 * `PageWrapper` styled component.
 */

const PageWrapper = styled.div`
  overflow: hidden;
  padding: calc(${theme('dimensions.navbarHeightMobile')}px + ${units(5)}) 0 ${units(13)};
  position: relative;

  ${media.min('ms')`
    padding: calc(${theme('dimensions.navbarHeight')}px + ${units(10)}) 0 ${units(25)};
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  ${media.min('ms')`
    display: grid;
    grid-template-areas:
      '. confirmAccount confirmAccount confirmAccount .'
      '. header         .              menu           .';
    grid-template-columns: 1fr 4fr 1fr 5fr 1fr;
  `}
`;

/**
 * `HeaderWrapper` styled component.
 */

const HeaderWrapper = styled.div`
  grid-area: header;

  ${media.max('ms')`
    margin-bottom: ${units(6)};
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H2).attrs({ as: 'h1' })`
  margin-bottom: ${units(1)};

  ${media.min('ms')`
    margin-bottom: ${units(3)};
  `}
`;

/**
 * `Lead` styled component.
 */

const Lead = styled(Type.H6).attrs({
  as: 'p',
  small: true
})`
  color: ${color('grey400')};
  margin-bottom: ${units(6)};

  ${media.min('ms')`
    margin-bottom: ${units(10)};
  `}
`;

/**
 * `Menu` styled component.
 */

const Menu = styled.div`
  grid-area: menu;
`;

/**
 * `SubscribeNewsletter` styled component.
 */

const SubscribeNewsletter = styled(CardTitle).attrs({ size: 'small' })`
  margin-bottom: ${units(2)};

  ${media.min('ms')`
    margin-bottom: ${units(3)};
  `}
`;

/**
 * `NewsletterWrapper` styled component.
 */

const NewsletterWrapper = styled.div`
  align-items: flex-start;
  display: flex;
  margin-bottom: ${units(6)};

  ${media.min('ms')`
    margin-bottom: ${units(10)};
  `}
`;

/**
 * `NewsletterText` styled component.
 */

const NewsletterText = styled(Type.H6).attrs({
  as: 'p',
  small: true
})`
  color: ${color('grey400')};
  flex: 1;
  padding-right: ${units(2)};
`;

/**
 * `useLogout` hook.
 */

function useLogout() {
  const router = useRouter();
  const { translate } = useTranslate();
  const { showMessage } = useSnackbar();
  const { onClearToken, token } = useAuth();
  const { isLoading, mutate } = useMutation(revokeToken);
  const handleLogout = useCallback(() => {
    mutate({ token }, {
      onSuccess: () => {
        onClearToken();
        showMessage(translate('my-revigres:dashboard.logout.successMessage'), {
          appearance: 'success'
        });

        router.push(authRoutes.signIn);
      }
    });
  }, [mutate, onClearToken, router, showMessage, token, translate]);

  return { isLoading, logout: handleLogout };
}

/**
 * `MyRevigres` page.
 */

const MyRevigres = (): ReactElement => {
  const { translate } = useTranslate();
  const { token, user } = useAuth();
  const { isLoading: isLoadingLogout, logout } = useLogout();
  const [isOpenUserDataSidebar, setIsOpenUserDataSidebar] = useState<boolean>();
  const [isOpenChangePasswordSidebar, setIsOpenChangePasswordSidebar] = useState<boolean>();
  const [newsletterToggled, setNewsletterToggled] = useState<boolean>(user?.user?.isSubscribedToNewsletter);
  const title = translate('my-revigres:dashboard.title');
  const description = translate('my-revigres:dashboard.description');
  const {
    isSubmitting,
    onSubscribeNewsletter,
    onUnsubscribeNewsletter
  } = useNewsletter();

  const { executeRecaptcha } = useGoogleReCaptcha();
  const handleNewsletterToggle = useCallback(async () => {
    const formData = {
      email: user?.authUser?.email,
      recaptchaToken: await executeRecaptcha('newsletter').then(token => token)
    };

    if (newsletterToggled) {
      onUnsubscribeNewsletter(formData).then(() => {
        setNewsletterToggled(false);
      });

      return;
    }

    onSubscribeNewsletter({
      ...formData,
      countryAlpha2: user?.user?.country.alpha2,
      name: user?.user?.name,
      professional: user?.user?.type === userType.professional
    }).then(() => {
      setNewsletterToggled(true);
    });
  }, [
    executeRecaptcha,
    newsletterToggled,
    onSubscribeNewsletter,
    onUnsubscribeNewsletter,
    user
  ]);

  useNavbarTheme({ theme: 'dark' });

  return (
    <>
      <Metatags
        description={description}
        pageTitle={title}
      />

      <PageWrapper>
        <Container>
          <Grid>
            {isEmpty(user?.authUser?.confirmedAt) && (
              <Box
                gridAreaMs={'confirmAccount'}
                marginBottom={units(6)}
                marginBottomMs={units(10)}
              >
                <ConfirmAccount token={token} />
              </Box>
            )}

            <HeaderWrapper>
              <Title>
                {title}
              </Title>

              <Lead>
                {description}
              </Lead>

              <SubscribeNewsletter>
                {translate('my-revigres:dashboard.subscribeNewsletter')}
              </SubscribeNewsletter>

              <NewsletterWrapper>
                <NewsletterText>
                  {translate('my-revigres:dashboard.newsletterText')}
                </NewsletterText>

                <SwitchButton
                  aria-label={translate('my-revigres:dashboard.newsletterText')}
                  checked={newsletterToggled}
                  disabled={isLoadingLogout || isSubmitting}
                  onToggle={handleNewsletterToggle}
                />
              </NewsletterWrapper>

              <Button
                isLoading={isLoadingLogout}
                onClick={logout}
                variant={'outlined'}
              >
                {translate('my-revigres:actions.logout')}
              </Button>
            </HeaderWrapper>

            <Menu>
              <MenuLink
                href={myRevigresRoutes.favorites}
                icon={favoriteIcon}
                title={translate('my-revigres:dashboard.menuOptions.favorites')}
                total={user?.user?.favoriteCount}
              />

              <MenuLink
                href={myRevigresRoutes.projects}
                icon={projectsIcon}
                title={translate('my-revigres:dashboard.menuOptions.projects')}
                total={user?.user?.projectCount}
              />

              <Permission userTypePermission={['professional', 'reseller']}>
                <MenuLink
                  href={myRevigresRoutes.clients}
                  icon={usersIcon}
                  title={translate('my-revigres:dashboard.menuOptions.clients')}
                  total={user?.user?.clientCount}
                />
              </Permission>

              <MenuLink
                icon={settingsIcon}
                onClick={() => setIsOpenUserDataSidebar(true)}
                title={translate('my-revigres:dashboard.menuOptions.personalData')}
              />

              <MenuLink
                icon={lockIcon}
                onClick={() => setIsOpenChangePasswordSidebar(true)}
                title={translate('my-revigres:dashboard.menuOptions.password')}
              />
            </Menu>
          </Grid>
        </Container>

        <UserDataSidebar
          isOpen={isOpenUserDataSidebar}
          onClose={() => setIsOpenUserDataSidebar(false)}
        />

        <ChangePasswordSidebar
          isOpen={isOpenChangePasswordSidebar}
          onClose={() => setIsOpenChangePasswordSidebar(false)}
        />
      </PageWrapper>
    </>
  );
};

/**
 * Export `getServerSideProps`.
 */

export const getServerSideProps = withSession();

/**
 * Export `MyRevigres` page.
 */

export default MyRevigres;
